import { useEffect, useRef, useState } from 'react';
import { storesApi, categoriesApi, type StoreBranch, type Category, type OsmPlace } from '../lib/domain';
import { StoreMap, type MapBounds } from '../components/StoreMap';
import { ApiError, OfflineError } from '../lib/api';
import { ScopeSwitcher } from '../components/ScopeSwitcher';
import { useScope } from '../lib/hooks';

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof OfflineError) return err.message;
  return 'Aktion fehlgeschlagen. Bitte erneut versuchen.';
}

export function StoresPage() {
  const [scope] = useScope();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<StoreBranch[]>([]);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{ latitude: number; longitude: number } | null>(null);
  const [mapPlaces, setMapPlaces] = useState<OsmPlace[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<OsmPlace | null>(null);
  const [mapMessage, setMapMessage] = useState<string | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const mapRequest = useRef(0);
  const [adoptedPlace, setAdoptedPlace] = useState<OsmPlace | null>(null);
  const [chainName, setChainName] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [conflict, setConflict] = useState<unknown>(null);

  const [orderingBranch, setOrderingBranch] = useState<StoreBranch | null>(null);
  const [orderedCategories, setOrderedCategories] = useState<Category[]>([]);
  const [orderMessage, setOrderMessage] = useState<string | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  const dragIndex = useRef<number | null>(null);
  const orderingRef = useRef<HTMLDivElement | null>(null);
  const newBranchRef = useRef<HTMLDetailsElement | null>(null);
  const [newBranchOpen, setNewBranchOpen] = useState(false);

  // Nutzeranfrage: bereits für Einkaufslisten verwendete Filialen sollen in
  // der Filialsuche schon vor einer Suche als Vorschlag erscheinen.
  useEffect(() => {
    if (!scope) return;
    storesApi
      .usedBranches(scope)
      .then((r) => setResults(r.branches))
      .catch(() => undefined);
  }, [scope]);

  async function search() {
    setSearchError(null);
    try {
      const r = await storesApi.searchBranches(query);
      setResults(r.branches);
      setSearched(true);
    } catch (err) {
      setSearchError(errorMessage(err));
    }
  }

  // Adresse/PLZ geocodieren und Karte dorthin setzen; die Geschäftssuche läuft dann über das Verschieben der Karte.
  async function mapSearch() {
    setSearchError(null);
    setMapMessage(null);
    try {
      const r = await storesApi.osmSearch(query);
      if (r.results.length === 0) {
        setMapMessage('Adresse oder PLZ nicht gefunden.');
        return;
      }
      setMapOpen(true);
      setMapCenter({ latitude: r.results[0].latitude, longitude: r.results[0].longitude });
    } catch (err) {
      setSearchError(errorMessage(err));
    }
  }

  async function searchBounds(bounds: MapBounds) {
    const id = ++mapRequest.current;
    // Kurze Wartezeit, damit beim Ziehen nicht jede Zwischenposition abgefragt wird.
    await new Promise((resolve) => setTimeout(resolve, 600));
    if (id !== mapRequest.current) return;
    setMapMessage('Suche Geschäfte im Kartenausschnitt …');
    try {
      const r = await storesApi.osmBounds(bounds);
      if (id !== mapRequest.current) return;
      setMapPlaces(r.results);
      setMapMessage(r.results.length === 0 ? 'Keine Geschäfte im Kartenausschnitt gefunden.' : null);
    } catch (err) {
      if (id === mapRequest.current) setMapMessage(errorMessage(err));
    }
  }

  async function createBranch(confirmDuplicate = false) {
    try {
      setConflict(null);
      await storesApi.createBranch({
        chainName,
        name,
        city,
        confirmDuplicate,
        ...(adoptedPlace && {
          address: adoptedPlace.address,
          postalCode: adoptedPlace.postalCode,
          latitude: adoptedPlace.latitude,
          longitude: adoptedPlace.longitude,
          osmId: adoptedPlace.osmId,
        }),
      });
      setAdoptedPlace(null);
      setChainName('');
      setName('');
      setCity('');
      await search();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setConflict(err.details);
      } else {
        alert(errorMessage(err));
      }
    }
  }

  function fillFromOsm(r: OsmPlace) {
    setAdoptedPlace(r);
    setChainName(r.name ?? r.displayName.split(',')[0]);
    setName(r.address ? `${r.name ?? r.displayName.split(',')[0]} ${r.address}` : r.displayName.split(',')[0]);
    setCity(r.city ?? '');
    // Formular „Neue Filiale“ aufklappen und in den Blick scrollen, damit klar ist, wie es weitergeht.
    setNewBranchOpen(true);
    requestAnimationFrame(() => newBranchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  // CAT-003/STORE-006: Kategorienreihenfolge je Filiale und Besitzbereich.
  async function openOrdering(branch: StoreBranch) {
    if (!scope) return;
    setOrderingBranch(branch);
    setOrderMessage(null);
    setOrderError(null);
    // Nutzeranfrage: beim Öffnen direkt zum Bearbeitungsbereich scrollen,
    // statt dass der Nutzer selbst danach suchen muss.
    requestAnimationFrame(() => orderingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    try {
      const [{ categories }, { order }] = await Promise.all([categoriesApi.list(scope), storesApi.categoryOrder(branch.id, scope)]);
      const rankById = new Map(order.map((o) => [o.categoryId, o.rank]));
      const sorted = [...categories].sort((a, b) => {
        const ra = rankById.get(a.id) ?? Number.MAX_SAFE_INTEGER;
        const rb = rankById.get(b.id) ?? Number.MAX_SAFE_INTEGER;
        return ra - rb || a.name.localeCompare(b.name);
      });
      setOrderedCategories(sorted);
    } catch (err) {
      setOrderError(errorMessage(err));
    }
  }

  useEffect(() => {
    if (orderingBranch) void openOrdering(orderingBranch);
    // openOrdering bewusst nicht als Dependency: soll nur bei Scope-Wechsel neu laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  function move(index: number, direction: -1 | 1) {
    setOrderedCategories((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function reorderTo(from: number, to: number) {
    setOrderedCategories((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  async function saveOrder() {
    if (!orderingBranch || !scope) return;
    setOrderMessage(null);
    setOrderError(null);
    try {
      const order = orderedCategories.map((c, index) => ({ categoryId: c.id, rank: index }));
      await storesApi.setCategoryOrder(orderingBranch.id, order, scope.startsWith('group:') ? scope.slice(6) : undefined);
      setOrderMessage('Reihenfolge gespeichert.');
    } catch (err) {
      setOrderError(errorMessage(err));
    }
  }

  return (
    <div className="stack">
      <h1>Geschäfte & Filialen</h1>
      <div className="card stack">
        <div className="field">
          <label htmlFor="store-query">Suche</label>
          <input id="store-query" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="row">
          <button onClick={search}>Vorhandene Filialen suchen</button>
          <button onClick={mapSearch}>Auf Karte suchen (Adresse/PLZ)</button>
        </div>
        {searchError && (
          <p className="error-text" role="alert">
            {searchError}
          </p>
        )}
      </div>

      {mapOpen && (
        <div className="card stack">
          <h2>Geschäfte auf der Karte</h2>
          <p className="muted">Karte verschieben oder zoomen, um im Ausschnitt zu suchen. Marker antippen, um ein Geschäft auszuwählen.</p>
          <StoreMap center={mapCenter} places={mapPlaces} onSelect={setSelectedPlace} onMoveEnd={(b) => void searchBounds(b)} />
          {mapMessage && <p className="muted">{mapMessage}</p>}
          {selectedPlace && (
            <div className="card stack">
              <strong>{selectedPlace.name ?? selectedPlace.displayName.split(',')[0]}</strong>
              <div className="muted">{selectedPlace.displayName}</div>
              <div className="row">
                <button className="primary" onClick={() => fillFromOsm(selectedPlace)}>
                  Geschäft übernehmen
                </button>
                <button onClick={() => setSelectedPlace(null)}>Abbrechen</button>
              </div>
            </div>
          )}
          <p className="muted">Daten und Karte: © OpenStreetMap-Mitwirkende</p>
        </div>
      )}

      <details className="card" ref={newBranchRef} open={newBranchOpen} onToggle={(e) => setNewBranchOpen(e.currentTarget.open)}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Neue Filiale</summary>
        <div className="stack" style={{ marginTop: 12 }}>
          <div className="field">
            <label htmlFor="chain">Handelskette</label>
            <input id="chain" value={chainName} onChange={(e) => setChainName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="branch-name">Filialname</label>
            <input id="branch-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="city">Ort</label>
            <input id="city" value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <button className="primary" onClick={() => createBranch(false)}>
            Anlegen
          </button>
          {conflict != null && (
            <div className="card">
              <p className="error-text">Mögliche Dublette gefunden.</p>
              <button onClick={() => createBranch(true)}>Trotzdem anlegen</button>
            </div>
          )}
        </div>
      </details>

      <h2>Gefundene Filialen</h2>
      {!searched && results.length > 0 && (
        <p className="muted">Bereits für Einkaufslisten verwendete Filialen (max. 10). Suche oben, um weitere zu finden.</p>
      )}
      <ul className="list-reset stack">
        {results.map((b) => (
          <li key={b.id} className="card">
            <div className="row between">
              <div>
                <strong>
                  {b.chain.name} - {b.name}
                </strong>
                {b.city && <div className="muted">{b.city}</div>}
                {b.listCount != null && <div className="muted">{b.listCount} Einkaufsliste(n)</div>}
              </div>
              <button onClick={() => openOrdering(b)}>Kategorien sortieren</button>
            </div>
          </li>
        ))}
        {results.length === 0 && <p className="muted">{searched ? 'Keine Filialen gefunden.' : 'Noch keine Suche durchgeführt.'}</p>}
      </ul>

      {orderingBranch && (
        <div className="card stack" ref={orderingRef}>
          <h2>
            Kategorienreihenfolge: {orderingBranch.chain.name} - {orderingBranch.name}
          </h2>
          <ScopeSwitcher />
          <p className="muted">
            Legt fest, in welcher Reihenfolge Kategorien auf Einkaufslisten für diese Filiale angezeigt werden (CAT-003/CAT-004),
            getrennt je Besitzbereich. Einträge können per Pfeiltasten oder per Ziehen (Drag & Drop) umsortiert werden.
          </p>
          {orderError && (
            <p className="error-text" role="alert">
              {orderError}
            </p>
          )}
          <ul className="list-reset stack">
            {orderedCategories.map((c, index) => (
              <li
                key={c.id}
                className="row between card"
                draggable
                onDragStart={() => {
                  dragIndex.current = index;
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex.current != null) reorderTo(dragIndex.current, index);
                  dragIndex.current = null;
                }}
                onDragEnd={() => {
                  dragIndex.current = null;
                }}
                style={{ cursor: 'grab' }}
              >
                <span>
                  <span aria-hidden="true" style={{ marginRight: 8, color: 'var(--color-muted, #888)' }}>
                    ⠿
                  </span>
                  {c.name}
                </span>
                <div className="row">
                  <button onClick={() => move(index, -1)} disabled={index === 0} aria-label={`${c.name} nach oben`}>
                    ↑
                  </button>
                  <button onClick={() => move(index, 1)} disabled={index === orderedCategories.length - 1} aria-label={`${c.name} nach unten`}>
                    ↓
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <div className="row">
            <button className="primary" onClick={saveOrder}>
              Reihenfolge speichern
            </button>
            <button onClick={() => setOrderingBranch(null)}>Schließen</button>
          </div>
          {orderMessage && <p className="muted">{orderMessage}</p>}
        </div>
      )}
    </div>
  );
}
